import { useForm } from 'react-hook-form'
import {
  Button,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import useCredentialedDataProviderOrgs from '@/hooks/useCredentialedDataProviderOrgs'
import { OverlayState } from '@/hooks/useOverlay'
import { Organization } from '@/types/organizations'
import { Project } from '@/types/projects'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { PixyDocsForm } from '@/components/forms'
import FormRadioGroup from '@/components/forms/FormRadioGroup'

// Distinct from any real org id so it can share the same string-valued
// RadioGroup as the company options.
export const ALL_COMPANIES_VALUE = '__all_companies__'

type FormValues = {
  target: string
}

const defaultValues: FormValues = { target: '' }

type NewRollupConfigDialogProps = {
  overlay: OverlayState
  project: Project
  dataProviderOrgs: Organization[]
  hasAllCompaniesConfig: boolean
  onCreate: (dpOrgId: string | null) => void
}

export default function NewRollupConfigDialog({
  overlay,
  project,
  dataProviderOrgs,
  hasAllCompaniesConfig,
  onCreate,
}: NewRollupConfigDialogProps) {
  const methods = useForm<FormValues>({ defaultValues })
  const {
    reset,
    formState: { isValid },
  } = methods

  const { credentialedDataProviderOrgs: companyOptions } =
    useCredentialedDataProviderOrgs({
      project,
      dataProviderOrgs,
      enabled: overlay.isOpen,
    })

  const resetState = () => reset(defaultValues)

  const handleSubmit = ({ target }: FormValues) => {
    if (!target) return
    overlay.close()
    onCreate(target === ALL_COMPANIES_VALUE ? null : target)
  }

  return (
    <Dialog
      {...overlay}
      onClose={resetState}
      title="New Rollup Config"
      subtitle="Choose the company this config targets — this can't be changed later."
    >
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <FormRadioGroup name="target" required>
            <FormControlLabel
              value={ALL_COMPANIES_VALUE}
              disabled={hasAllCompaniesConfig}
              control={<Radio />}
              label={
                <Stack sx={{ py: 0.5 }}>
                  <Typography variant="body2">All Companies</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hasAllCompaniesConfig
                      ? 'Already exists — only one All Companies config is allowed'
                      : 'Applies to all vendors'}
                  </Typography>
                </Stack>
              }
            />
            {companyOptions.map((company) => (
              <FormControlLabel
                key={company.id}
                value={company.id}
                control={<Radio />}
                label={
                  <Typography variant="body2" sx={{ py: 0.5 }}>
                    {company.name}
                  </Typography>
                }
              />
            ))}
          </FormRadioGroup>
        </DialogContent>

        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!isValid}>
            Create config
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
